import { forwardRef } from 'react';
import { IconStyle } from '../Icons/Icons';
import { type IconPropInterface } from '../types/types';
import RawTextLink from './RawTextLink';
import { TextLinkIconPosition, TextLinkStyle, TextLinkUnderlineStyle, TextLinkSize } from './types/enums';
import type { TextLinkPropsTypes, TextLinkTheme } from './types/propsType';

const getIcon = (iconPosition: TextLinkIconPosition, position: TextLinkIconPosition, icon?: IconPropInterface) =>
  iconPosition === position || iconPosition === TextLinkIconPosition.BOTH
    ? {
        iconName: `chevron-${position}`,
        iconStyle: IconStyle.SOLID,
        ...icon,
      }
    : undefined;

/**
 * @description TextLink provide anchor tag wrapped with style;
 * Props -> text (Required), href (Required), theme ,target and others.
 * Prop -> target dafault value is _self.
 */
const TextLink = forwardRef<HTMLAnchorElement, TextLinkPropsTypes>(
  ({ textLinkStyle, ...restProps }: TextLinkPropsTypes, ref) => {
    const { CHEVRON, CHEVRONUNDERLINE, ICON, UNDERLINE, ICONUNDERLINE } = TextLinkStyle;
    const { HOVER, FIXED } = TextLinkUnderlineStyle;
    const { LEFT, RIGHT } = TextLinkIconPosition;
    const customProps = { underline: HOVER, iconPosition: LEFT, ...restProps };
    const { iconPosition, leftIcon, rightIcon } = customProps;

    switch (textLinkStyle) {
      case CHEVRONUNDERLINE:
        customProps.leftIcon = getIcon(iconPosition, LEFT) as IconPropInterface;
        customProps.rightIcon = getIcon(iconPosition, RIGHT) as IconPropInterface;
        customProps.underline = FIXED;
        break;
      case UNDERLINE:
        customProps.leftIcon = customProps.rightIcon = undefined;
        customProps.underline = FIXED;
        break;
      case ICONUNDERLINE:
        customProps.leftIcon = getIcon(iconPosition, LEFT, leftIcon) as IconPropInterface;
        customProps.rightIcon = getIcon(iconPosition, RIGHT, rightIcon) as IconPropInterface;
        customProps.underline = FIXED;
        break;
      case ICON:
        customProps.leftIcon = getIcon(iconPosition, LEFT, leftIcon) as IconPropInterface;
        customProps.rightIcon = getIcon(iconPosition, RIGHT, rightIcon) as IconPropInterface;
        break;
      case CHEVRON:
        customProps.leftIcon = getIcon(iconPosition, LEFT) as IconPropInterface;
        customProps.rightIcon = getIcon(iconPosition, RIGHT) as IconPropInterface;
        break;
      default:
        customProps.leftIcon = customProps.rightIcon = undefined;
        break;
    }
    return <RawTextLink {...customProps} forwardedRef={ref} />;
  },
);
TextLink.displayName = 'TextLink';

export { TextLinkIconPosition, TextLinkStyle, TextLinkUnderlineStyle, TextLinkSize };
export type { TextLinkPropsTypes, TextLinkTheme };
export default TextLink;
